import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList,
  SkillsBlock, ContactRow, EmptyState,
  renderDynamicSection,
} from './templateDesignSystem';

export const ATS_META = {
  atsRating: 99,
  atsLabel: 'ATS Perfect',
  bestFor: 'Any role — maximum readability',
  compatibility: 'Works with all major ATS systems',
  recommendedFor: 'All professionals, mid-senior level',
};

function CleanMinimalATSTemplate({
  resumeData,
  accentColor = '#111827',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => {},
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const {
    name, role, summaryText, skills, experience, internships,
    projects, educationList, certList, achieveList, extraList, languageList,
  } = d;

  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const SectionTitle = ({ title }) => (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      <h2 style={{
        fontSize: fs('10px'), fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '2px', color: accentColor, margin: 0,
        paddingBottom: 4, borderBottom: `1.5px solid ${accentColor}`,
        fontFamily: 'inherit',
      }}>{title}</h2>
    </div>
  );

  const SECTION_RENDERERS = {
    summary: () => (summaryText || editMode) ? (
      <section data-ats="summary" key="summary" style={{ marginBottom: 10 }}>
        <SectionTitle title="Summary" />
        <EditableField
          editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
          tag="p" multiline
          style={{ fontSize: fs('10px'), color: '#374151', lineHeight: 1.7, margin: 0, textAlign: 'justify' }}
          placeholder="Write a professional summary..."
        />
      </section>
    ) : null,

    skills: () => (skills.length > 0) ? (
      <section data-ats="skills" key="skills" style={{ marginBottom: 10 }}>
        <SectionTitle title="Skills" />
        <SkillsBlock skills={skills} accentColor={accentColor} layout="rows" />
      </section>
    ) : null,

    experience: () => (experience.length > 0) ? (
      <section data-ats="experience" key="experience" style={{ marginBottom: 10 }}>
        <SectionTitle title="Experience" />
        {experience.map((exp, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                {exp.company && <span style={{ color: '#6B7280', fontSize: fs('10px') }}>{' | '}{exp.company}</span>}
              </div>
              <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                tag="span" style={{ fontSize: fs('9px'), color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 8, fontWeight: 500 }} />
            </div>
            <BulletList points={exp.points} accentColor={accentColor} />
          </div>
        ))}
      </section>
    ) : null,

    internships: () => (internships.length > 0) ? (
      <section data-ats="experience" key="internships" style={{ marginBottom: 10 }}>
        <SectionTitle title="Internships" />
        {internships.map((exp, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                {exp.company && <span style={{ color: '#6B7280', fontSize: fs('10px') }}>{' | '}{exp.company}</span>}
              </div>
              <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                tag="span" style={{ fontSize: fs('9px'), color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 8, fontWeight: 500 }} />
            </div>
            <BulletList points={exp.points} accentColor={accentColor} />
          </div>
        ))}
      </section>
    ) : null,

    projects: () => (projects.length > 0) ? (
      <section data-ats="projects" key="projects" style={{ marginBottom: 10 }}>
        <SectionTitle title="Projects" />
        {projects.map((proj, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111' }}>{proj.title || proj.name}</span>
                {proj.tech?.length > 0 && (
                  <span style={{ fontSize: fs('9px'), color: accentColor, marginLeft: 6, fontWeight: 500 }}>
                    [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                  </span>
                )}
              </div>
            </div>
            <BulletList points={proj.points} accentColor={accentColor} />
          </div>
        ))}
      </section>
    ) : null,

    education: () => (educationList.length > 0) ? (
      <section data-ats="education" key="education" style={{ marginBottom: 10 }}>
        <SectionTitle title="Education" />
        {educationList.map((edu, idx) => (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div>
              <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
              <span style={{ color: '#6B7280', fontSize: fs('10px') }}>{' | '}{edu?.college}</span>
              {edu?.cgpa && <span style={{ fontSize: fs('9px'), color: '#9CA3AF', marginLeft: 6 }}>({edu.cgpa})</span>}
            </div>
            <EditableField editMode={editMode} value={edu?.year || ''} onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
              tag="span" style={{ fontSize: fs('9px'), color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 8 }} />
          </div>
        ))}
      </section>
    ) : null,

    certifications: () => (certList.length > 0) ? (
      <section data-ats="certifications" key="certifications" style={{ marginBottom: 10 }}>
        <SectionTitle title="Certifications" />
        <BulletList points={certList} accentColor={accentColor} />
      </section>
    ) : null,

    achievements: () => (achieveList.length > 0) ? (
      <section data-ats="achievements" key="achievements" style={{ marginBottom: 10 }}>
        <SectionTitle title="Achievements" />
        <BulletList points={achieveList} accentColor={accentColor} />
      </section>
    ) : null,

    languages: () => (languageList.length > 0) ? (
      <section data-ats="languages" key="languages" style={{ marginBottom: 10 }}>
        <SectionTitle title="Languages" />
        <p style={{ fontSize: fs('10px'), color: '#374151', margin: 0 }}>{languageList.join('   ·   ')}</p>
      </section>
    ) : null,

    extracurricular: () => (extraList.length > 0) ? (
      <section data-ats="extracurricular" key="extracurricular" style={{ marginBottom: 10 }}>
        <SectionTitle title="Extracurricular Activities" />
        <BulletList points={extraList} accentColor={accentColor} />
      </section>
    ) : null,
  };

  const DEFAULT_ORDER = ['summary', 'skills', 'experience', 'internships', 'projects', 'education', 'certifications', 'achievements', 'languages', 'extracurricular'];
  const orderedKeys = Array.isArray(resumeData.sections) && resumeData.sections.length > 0
    ? resumeData.sections
    : DEFAULT_ORDER;

  return (
    <div
      id="resume"
      style={{
        background: '#fff',
        color: '#111',
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: fs('10px'),
        lineHeight: 1.5,
        padding: '24px 30px',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* ─── Header ─── */}
      <header style={{ textAlign: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: `2px solid ${accentColor}` }}>
        <EditableField
          editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)}
          tag="h1"
          style={{ fontSize: fs('24px'), fontWeight: 800, color: '#111', margin: 0, letterSpacing: '-0.5px' }}
          placeholder="Your Name"
        />
        {(role || editMode) && (
          <EditableField
            editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)}
            tag="p"
            style={{ fontSize: fs('11px'), fontWeight: 500, color: '#6B7280', margin: '4px 0 0', letterSpacing: '1.5px', textTransform: 'uppercase' }}
            placeholder="Your Professional Title"
          />
        )}
        <ContactRow data={d} accentColor={accentColor} />
      </header>

      {orderedKeys.map(key => SECTION_RENDERERS[key] ? SECTION_RENDERERS[key]() : renderDynamicSection({ sectionId: key, resumeData, accentColor, editMode, onFieldUpdate, fs }))}
    </div>
  );
}

export default CleanMinimalATSTemplate;
